import axios from "axios";
import {
  ClientBillingReport,
  ClientBillingReportSettings,
  ClientBillingReportSettingsApi,
  UpdateClientBillingSettings,
} from "../../Types/ClientBilling";

// Used for Connect Billing Reports
export const getBillingHistory = (clientId: string | null, month: string | null) => {
  return axios
    .get(import.meta.env.VITE_BACKEND_API + "/api/BillingReport/getHistory/" + clientId + "/" + month)
    .then((res) => res.data);
};

export const getBillingReportNotificationSettings = async () => {
  const res = await axios.get<ClientBillingReportSettingsApi>(
    import.meta.env.VITE_CLIENTSERVICE_API + "/client/billingsettings"
  );
  var response = res?.data;
  if (response !== null) {
    return {
      ...response,
      modifiedDate: response.modifiedDate !== null ? new Date(response.modifiedDate) : response.modifiedDate,
      scheduledTime:
        response.scheduledTime !== null
          ? getScheduledTimeAsEst(response.scheduledTime)
          : response.scheduledTime,
    } as ClientBillingReportSettings;
  }
  return response;
};

export const updateBillingReportNotificationSettings = async (settings: UpdateClientBillingSettings) => {
  const res = await axios.put<ClientBillingReportSettings>(
    import.meta.env.VITE_CLIENTSERVICE_API + "/client/updatebillingsettings",
    { ...settings, scheduledTime: prepScheduledTimeAsEst(settings.scheduledTime) }
  );
  return res.data;
};

// Used for Client Service Billing Reports
export const getClientBillingHistory = async (clientId?: string | null, month?: string | null) => {
  const clientIdParam = !!clientId ? `clientId=${clientId}` : "";

  let startDateParam = "";
  let endDateParam = "";
  if (!!month) {
    const monthDate = new Date(month);
    const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).toISOString().split("T")[0];
    const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];
    startDateParam = `startDate=${firstDay}T00:00:00.000Z`;
    endDateParam = `endDate=${lastDay}T23:59:59.999Z`;
  }

  const queryString = [clientIdParam, startDateParam, endDateParam].filter((x) => !!x).join("&");

  const res = await axios.get<ClientBillingReport[]>(
    import.meta.env.VITE_CLIENTSERVICE_API + "/getclientbillingreports?" + queryString
  );
  return res.data;
};

function getScheduledTimeAsEst(date: string | Date) {
  // Saved settings is time only as UTC, extract hours and minutes, then convert to EST
  const currentBrowserDateTime = new Date();
  const estOffset = getEasternOffsetHours();

  // String will be in format "HH:MM:SS", convert to numbers or if it's a Date already, get the hours and minutes
  let hours, minutes;
  if (typeof date === "string") {
    [hours, minutes] = date.split(":").map(Number);
  } else {
    hours = date.getHours();
    minutes = date.getMinutes();
  }

  // Set scheduled time based on users current date-time, but with the hours and minutes from the saved settings
  const scheduledTime = new Date(
    currentBrowserDateTime.getFullYear(),
    currentBrowserDateTime.getMonth(),
    currentBrowserDateTime.getDate(),
    hours,
    minutes
  );

  // Subtract the offset so the rendered time will read as EST regardless of user's timezone
  scheduledTime.setHours(scheduledTime.getHours() - estOffset);

  return scheduledTime;
}

// Date object will be based on user's timezone, but they are entering a time in EST, subtract the difference and return the time as EST
function prepScheduledTimeAsEst(date: string | Date) {
  // User's timezone difference from UTC in hours
  const userTimezoneDifference = new Date().getTimezoneOffset() / 60;

  // Input time is EST/EDT which is behind UTC, therefore subtract Eastern time difference from the user's timezone difference
  // Example, if user is PST, their timezone difference is 8, we subtract 5 if it's EST to get the desired timezone difference of 3
  const timezoneDifference = userTimezoneDifference - getEasternOffsetHours();

  // Set the hours based on what was entered, subtract the timezone difference to get the time in EST regardless of user's timezone
  if (typeof date === "string") {
    date = new Date(date);
  }
  
  // Use setHours to subtract the hour difference and automatically adjust the date if necessary
  date.setHours(date.getHours() - timezoneDifference);
  return date;
}

/**
 * Calculates the UTC offset for Eastern time in hours regardless of daylight savings time
 * @returns {number} The offset in hours
 */
function getEasternOffsetHours(): number {
  // Get the current date-time from the user's browser
  const now = new Date();

  // Get the current date-time in Eastern time
  const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));

  // Calculate the current offset in hours (could be 4 or 5 depending on daylight savings time)
  let offset = now.getUTCHours() - easternTime.getHours();

  // Adjust the offset if the time difference is more than 12 hours forwards or backwards
  // Example: If the user has an offset of +13 hours, it's actually -11 hours due to international date line
  if (offset > 12) {
    offset -= 24;
  } else if (offset < -12) {
    offset += 24;
  }

  return offset;
}
